import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Shield, AlertTriangle, Bell, Globe, Eye, Activity, DollarSign, Target, Zap, LineChart } from 'lucide-react';
import { fetchStablecoinData, fetchTreasuryData, fetchSocialData } from '../services/api';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [stablecoinData, setStablecoinData] = useState([]);
  const [treasuryData, setTreasuryData] = useState({});
  const [socialData, setSocialData] = useState({});
  const [loading, setLoading] = useState(true);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/signed-out';
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadData();
    const dataTimer = setInterval(loadData, 5 * 60 * 1000); // Refresh every 5 minutes
    return () => clearInterval(dataTimer);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [stablecoinResult, treasuryResult, socialResult] = await Promise.all([
        fetchStablecoinData(),
        fetchTreasuryData(),
        fetchSocialData()
      ]);
      setStablecoinData(stablecoinResult);
      setTreasuryData(treasuryResult);
      setSocialData(socialResult);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (risk) => {
    if (risk < 30) return '#10b981';
    if (risk < 60) return '#f59e0b';
    return '#ef4444';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'stable': return '#10b981';
      case 'watch': return '#f59e0b';
      case 'risk': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'deep-monitor', label: 'Deep Monitoring', icon: Target },
    { id: 'treasury', label: 'Policy & Treasury', icon: TrendingUp },
    { id: 'social', label: 'Social Intel', icon: Globe },
    { id: 'alerts', label: 'Alerts', icon: Bell }
  ];

  const renderStablecoinTicker = () => (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      borderRadius: '16px',
      padding: '24px',
      marginBottom: '24px'
    }}>
      <h3 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <DollarSign size={20} color="#3dd6d0" />
        Stablecoin Monitor
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
        {stablecoinData.map(coin => (
          <div key={coin.symbol} style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '12px',
            padding: '16px',
            border: `1px solid ${getStatusColor(coin.status)}40`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontWeight: 'bold', fontSize: '18px' }}>{coin.symbol}</span>
              <span style={{
                padding: '4px 8px',
                borderRadius: '6px',
                fontSize: '12px',
                background: `${getStatusColor(coin.status)}20`,
                color: getStatusColor(coin.status)
              }}>
                {coin.status.toUpperCase()}
              </span>
            </div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
              ${coin.price.toFixed(4)}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#a7b6c7' }}>
              <span style={{ color: coin.change >= 0 ? '#10b981' : '#ef4444' }}>
                {coin.change >= 0 ? '+' : ''}{coin.change.toFixed(2)}%
              </span>
              <span>Vol: {coin.volume}</span>
            </div>
            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                <span>Risk Score</span>
                <span>{coin.risk}</span>
              </div>
              <div style={{
                width: '100%',
                height: '6px',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '3px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${coin.risk}%`,
                  height: '100%',
                  background: getRiskColor(coin.risk),
                  transition: 'width 0.5s ease'
                }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDeepMonitor = () => (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      borderRadius: '16px',
      padding: '24px',
      marginBottom: '24px'
    }}>
      <h3 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Target size={20} color="#3dd6d0" />
        Deep Peg Analysis
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        <div>
          <h4 style={{ color: '#a7b6c7', margin: '0 0 16px 0' }}>Deviation from $1.00</h4>
          {stablecoinData.map(coin => {
            const deviation = ((coin.price - 1) * 100).toFixed(3);
            return (
              <div key={coin.symbol} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                marginBottom: '8px'
              }}>
                <span>{coin.symbol}</span>
                <span style={{
                  color: Math.abs(deviation) < 0.1 ? '#10b981' : Math.abs(deviation) < 0.5 ? '#f59e0b' : '#ef4444',
                  fontFamily: 'monospace'
                }}>
                  {deviation > 0 ? '+' : ''}{deviation}¢
                </span>
              </div>
            );
          })}
        </div>
        <div>
          <h4 style={{ color: '#a7b6c7', margin: '0 0 16px 0' }}>24h Volatility</h4>
          {stablecoinData.map(coin => (
            <div key={coin.symbol} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '8px',
              marginBottom: '8px'
            }}>
              <span>{coin.symbol}</span>
              <div style={{
                width: '60px',
                height: '8px',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${Math.min(Math.abs(coin.change) * 10, 100)}%`,
                  height: '100%',
                  background: '#3dd6d0'
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTreasuryPanel = () => (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      borderRadius: '16px',
      padding: '24px',
      marginBottom: '24px'
    }}>
      <h3 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <LineChart size={20} color="#3dd6d0" />
        Treasury Risk Monitor
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: '#a7b6c7', marginBottom: '8px' }}>2-Year Treasury</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#3dd6d0' }}>
            {treasuryData.twoYear?.toFixed(2)}%
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: '#a7b6c7', marginBottom: '8px' }}>10-Year Treasury</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#3dd6d0' }}>
            {treasuryData.tenYear?.toFixed(2)}%
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: '#a7b6c7', marginBottom: '8px' }}>Yield Spread</div>
          <div style={{ 
            fontSize: '28px', 
            fontWeight: 'bold',
            color: treasuryData.spread > 0 ? '#10b981' : treasuryData.spread < -0.5 ? '#ef4444' : '#f59e0b'
          }}>
            {treasuryData.spread?.toFixed(2)}%
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: '#a7b6c7', marginBottom: '8px' }}>Risk Score</div>
          <div style={{ 
            fontSize: '28px', 
            fontWeight: 'bold',
            color: getRiskColor(treasuryData.riskScore || 0)
          }}>
            {Math.round(treasuryData.riskScore || 0)}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSocialIntelDashboard = () => {
    // Enhanced social data following the whitepaper schema
    const socialIntelData = {
      overallSentiment: socialData.sentiment || 0.47,
      totalMentions: socialData.mentions || 3299,
      credibilityScore: socialData.credibilityScore || 0.78,
      socialRiskScores: {
        USDT: 72.4,
        USDC: 23.1, 
        DAI: 18.7,
        TUSD: 64.2,
        FRAX: 31.5
      },
      topSources: [
        {
          platform: 'CoinDesk',
          credibility: 89.2,
          sentiment: 0.32,
          summary: 'Reports growing regulatory scrutiny on USDT reserves, citing unnamed sources close to Treasury investigations.',
          timestamp: '2 hours ago'
        },
        {
          platform: 'The Block',
          credibility: 85.7,
          summary: 'Analysis suggests institutional confidence in USDC remains stable despite broader market volatility.',
          sentiment: 0.71,
          timestamp: '4 hours ago'
        },
        {
          platform: 'DeFi Pulse',
          credibility: 76.4,
          summary: 'DAI peg mechanism showing resilience, MakerDAO governance discussions on PSM adjustments ongoing.',
          sentiment: 0.68,
          timestamp: '6 hours ago'
        },
        {
          platform: 'Decrypt',
          credibility: 72.1,
          summary: 'TUSD facing questions over attestation delays, some DeFi protocols considering risk parameter changes.',
          sentiment: 0.29,
          timestamp: '8 hours ago'
        }
      ],
      topInfluencers: [
        {
          handle: '@DeFiDave',
          platform: 'twitter',
          credibility: 91.5,
          followers: '127K',
          recentTake: 'Seeing unusual USDT redemption patterns. Not panic level, but worth monitoring closely. Historical data suggests...',
          sentiment: 0.35,
          timestamp: '3h ago',
          engagement: '2.4K likes, 847 retweets'
        },
        {
          handle: '@StablecoinSarah',
          platform: 'twitter', 
          credibility: 88.9,
          followers: '89K',
          recentTake: 'USDC backing transparency continues to set the standard. Circle\'s monthly attestations show consistent 1:1 backing with...',
          sentiment: 0.78,
          timestamp: '5h ago',
          engagement: '1.8K likes, 623 retweets'
        },
        {
          handle: '@CryptoQuant_com',
          platform: 'twitter',
          credibility: 94.2,
          followers: '234K', 
          recentTake: 'On-chain data shows DAI stability mechanism working as designed. PSM utilization at healthy levels despite...',
          sentiment: 0.72,
          timestamp: '7h ago',
          engagement: '3.1K likes, 1.2K retweets'
        }
      ],
      sentimentHistory: [
        { time: '00:00', value: 52 },
        { time: '04:00', value: 48 },
        { time: '08:00', value: 45 },
        { time: '12:00', value: 42 },
        { time: '16:00', value: 47 },
        { time: '20:00', value: 47 }
      ]
    };

    return (
      <div style={{ display: 'grid', gap: '24px' }}>
        {/* Overall Sentiment & Risk Scores */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Globe size={20} color="#3dd6d0" />
            Social Intelligence Overview
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '32px' }}>
            <div>
              <h4 style={{ color: '#a7b6c7', margin: '0 0 16px 0', fontSize: '14px' }}>Market Sentiment "Feel"</h4>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '48px',
                  fontWeight: 'bold',
                  color: socialIntelData.overallSentiment > 0.6 ? '#10b981' : socialIntelData.overallSentiment > 0.4 ? '#f59e0b' : '#ef4444',
                  marginBottom: '8px'
                }}>
                  {Math.round(socialIntelData.overallSentiment * 100)}
                </div>
                <div style={{ fontSize: '12px', color: '#a7b6c7', marginBottom: '16px' }}>
                  {socialIntelData.overallSentiment > 0.6 ? 'Positive' : socialIntelData.overallSentiment > 0.4 ? 'Neutral' : 'Negative'} Sentiment
                </div>
                <div style={{
                  width: '100%',
                  height: '12px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: `${socialIntelData.overallSentiment * 100}%`,
                    height: '100%',
                    background: socialIntelData.overallSentiment > 0.6 ? '#10b981' : socialIntelData.overallSentiment > 0.4 ? '#f59e0b' : '#ef4444',
                    transition: 'width 0.5s ease'
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#a7b6c7' }}>
                  <span>Mentions: {socialIntelData.totalMentions.toLocaleString()}</span>
                  <span>Credibility: {Math.round(socialIntelData.credibilityScore * 100)}</span>
                </div>
              </div>
            </div>
            <div>
              <h4 style={{ color: '#a7b6c7', margin: '0 0 16px 0', fontSize: '14px' }}>Sentiment Trend (24h)</h4>
              <div style={{ position: 'relative', height: '120px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '16px' }}>
                <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
                  <polyline
                    points={socialIntelData.sentimentHistory.map((point, i) => 
                      `${(i / (socialIntelData.sentimentHistory.length - 1)) * 100}%,${100 - point.value}%`
                    ).join(' ')}
                    fill="none"
                    stroke="#3dd6d0"
                    strokeWidth="2"
                  />
                  {socialIntelData.sentimentHistory.map((point, i) => (
                    <circle
                      key={i}
                      cx={`${(i / (socialIntelData.sentimentHistory.length - 1)) * 100}%`}
                      cy={`${100 - point.value}%`}
                      r="3"
                      fill="#3dd6d0"
                    />
                  ))}
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Social Risk Scores by Asset */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertTriangle size={20} color="#f59e0b" />
            Social Risk Scores
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
            {Object.entries(socialIntelData.socialRiskScores).map(([symbol, score]) => (
              <div key={symbol} style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
                border: `1px solid ${score > 60 ? '#ef444420' : score > 30 ? '#f59e0b20' : '#10b98120'}`
              }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>{symbol}</div>
                <div style={{
                  fontSize: '32px',
                  fontWeight: 'bold',
                  color: score > 60 ? '#ef4444' : score > 30 ? '#f59e0b' : '#10b981',
                  marginBottom: '8px'
                }}>
                  {score.toFixed(1)}
                </div>
                <div style={{
                  width: '100%',
                  height: '6px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${score}%`,
                    height: '100%',
                    background: score > 60 ? '#ef4444' : score > 30 ? '#f59e0b' : '#10b981'
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Sources Summary */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BarChart3 size={20} color="#3dd6d0" />
            Source Intelligence
          </h3>
          <div style={{ display: 'grid', gap: '16px' }}>
            {socialIntelData.topSources.map((source, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '12px',
                padding: '20px',
                borderLeft: `4px solid ${source.sentiment > 0.6 ? '#10b981' : source.sentiment > 0.4 ? '#f59e0b' : '#ef4444'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{source.platform}</span>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      background: 'rgba(61,214,208,0.2)',
                      color: '#3dd6d0'
                    }}>
                      Credibility: {source.credibility}
                    </span>
                  </div>
                  <span style={{ fontSize: '12px', color: '#a7b6c7' }}>{source.timestamp}</span>
                </div>
                <p style={{ margin: '0', fontSize: '14px', lineHeight: '1.5', color: '#e6eef8' }}>
                  {source.summary}
                </p>
                <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <span style={{ fontSize: '12px', color: '#a7b6c7' }}>
                    Sentiment: <span style={{ color: source.sentiment > 0.6 ? '#10b981' : source.sentiment > 0.4 ? '#f59e0b' : '#ef4444' }}>
                      {source.sentiment > 0.6 ? 'Positive' : source.sentiment > 0.4 ? 'Neutral' : 'Negative'}
                    </span>
                  </span>
                  <div style={{
                    width: '60px',
                    height: '6px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${source.sentiment * 100}%`,
                      height: '100%',
                      background: source.sentiment > 0.6 ? '#10b981' : source.sentiment > 0.4 ? '#f59e0b' : '#ef4444'
                    }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Influencers */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Activity size={20} color="#3dd6d0" />
            Top-Rated Influencers
          </h3>
          <div style={{ display: 'grid', gap: '20px' }}>
            {socialIntelData.topInfluencers.map((influencer, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '12px',
                padding: '20px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#3dd6d0' }}>
                        {influencer.handle}
                      </div>
                      <div style={{ fontSize: '12px', color: '#a7b6c7' }}>
                        {influencer.followers} followers • Credibility: {influencer.credibility}
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize: '12px', color: '#a7b6c7' }}>{influencer.timestamp}</span>
                </div>
                <p style={{ 
                  margin: '0 0 12px 0', 
                  fontSize: '14px', 
                  lineHeight: '1.5', 
                  color: '#e6eef8',
                  fontStyle: 'italic'
                }}>
                  "{influencer.recentTake}"
                </p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: '#a7b6c7' }}>
                  <span>{influencer.engagement}</span>
                  <span style={{ 
                    color: influencer.sentiment > 0.6 ? '#10b981' : influencer.sentiment > 0.4 ? '#f59e0b' : '#ef4444'
                  }}>
                    {influencer.sentiment > 0.6 ? 'Bullish' : influencer.sentiment > 0.4 ? 'Neutral' : 'Bearish'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(1200px 600px at 10% 10%, rgba(61,214,208,0.06), transparent), radial-gradient(1000px 600px at 90% 20%, rgba(239,71,111,0.05), transparent), linear-gradient(135deg,#0b132b,#1c2541)',
      color: '#e6eef8',
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '24px',
        borderBottom: '1px solid rgba(255,255,255,0.08)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Shield size={32} color="#3dd6d0" />
          <div>
            <h1 style={{ margin: 0, fontSize: '24px' }}>StableGuard Dashboard</h1>
            <div style={{ fontSize: '14px', color: '#a7b6c7' }}>
              {currentTime.toLocaleTimeString()} • System Status: {loading ? 'Loading...' : 'Live'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button style={{
            padding: '8px 16px',
            borderRadius: '999px',
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'transparent',
            color: '#e6eef8',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <AlertTriangle size={16} />
            Alerts (3)
          </button>
          <button 
            onClick={handleLogout}
            style={{
              padding: '8px 16px',
              borderRadius: '999px',
              border: '1px solid #ef476f',
              background: 'transparent',
              color: '#ef476f',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            Sign Out
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        padding: '0 24px'
      }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '12px 20px',
                  border: 'none',
                  background: activeTab === tab.id ? 'rgba(61,214,208,0.1)' : 'transparent',
                  color: activeTab === tab.id ? '#3dd6d0' : '#a7b6c7',
                  cursor: 'pointer',
                  borderBottom: activeTab === tab.id ? '2px solid #3dd6d0' : '2px solid transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px'
                }}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Main Content */}
      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {activeTab === 'overview' && (
          <>
            {renderStablecoinTicker()}
            {renderDeepMonitor()}
            {renderTreasuryPanel()}
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '16px',
              padding: '24px'
            }}>
              <h3 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Globe size={20} color="#3dd6d0" />
                Social Sentiment Overview
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', color: '#a7b6c7', marginBottom: '8px' }}>Sentiment Score</div>
                  <div style={{
                    fontSize: '36px',
                    fontWeight: 'bold',
                    color: socialData.sentiment > 0.6 ? '#10b981' : socialData.sentiment > 0.4 ? '#f59e0b' : '#ef4444'
                  }}>
                    {((socialData.sentiment || 0.47) * 100).toFixed(0)}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', color: '#a7b6c7', marginBottom: '8px' }}>Mentions (24h)</div>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#3dd6d0' }}>
                    {(socialData.mentions || 3299).toLocaleString()}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', color: '#a7b6c7', marginBottom: '8px' }}>Credibility</div>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#10b981' }}>
                    {((socialData.credibilityScore || 0.78) * 100).toFixed(0)}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        
        {activeTab === 'deep-monitor' && (
          <>
            {renderStablecoinTicker()}
            {renderDeepMonitor()}
          </>
        )}
        
        {activeTab === 'treasury' && renderTreasuryPanel()}
        {activeTab === 'social' && renderSocialIntelDashboard()}
        
        {activeTab === 'alerts' && (
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            borderRadius: '16px',
            padding: '24px'
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>Active Alerts</h3>
            <div style={{ color: '#a7b6c7', textAlign: 'center', padding: '40px' }}>
              Alert management system coming soon...
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;